import type {
  Priority,
  DayStr,
  MockCache,
  TaskId,
  UserId,
  Task,
  MilestoneId,
  TaskTypeId,
} from '../../types';
import { watchersOf, statusLabel, formatMinutes } from '../../helpers';

import type { FanoutContext } from '.';
import type { InboxWish } from '../inbox-delivery';
import { isTerminal } from '../internal';

const TASK_FIELD_LABELS: Record<string, string> = {
  title: 'заголовок',
  description: 'описание',
  priority: 'приоритет',
  estimate: 'оценка',
  dueOn: 'срок',
  startOn: 'начало',
  labels: 'метки',
  type: 'тип',
  status: 'статус',
  project: 'проект',
};
function taskFieldLabel(field: string): string {
  return TASK_FIELD_LABELS[field] ?? field;
}

const PRIORITY_LABELS: Record<Priority, string> = {
  urgent: 'срочный',
  high: 'высокий',
  medium: 'средний',
  low: 'низкий',
  none: 'без приоритета',
};

function formatDayRu(day: DayStr): string {
  const [y, m, d] = String(day).split('-').map(Number);
  return new Date(Date.UTC(y, (m || 1) - 1, d || 1)).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });
}

function taskChangeWatchers(cache: MockCache, taskId: TaskId): UserId[] {
  return watchersOf(cache, { kind: 'task', id: taskId })
    .filter((w) => !w.muted)
    .map((w) => w.userId);
}

function pushTaskChanged(wishes: InboxWish[], watcherIds: UserId[], task: Task, preview: string): void {
  for (const userId of watcherIds) {
    wishes.push({
      userId,
      kind: 'task_changed',
      sourceKind: 'task',
      sourceId: String(task.id),
      taskId: task.id,
      channelId: null,
      preview,
    });
  }
}

function pushStatusWishes(cache: MockCache, wishes: InboxWish[], task: Task): void {
  for (const watcher of watchersOf(cache, { kind: 'task', id: task.id })) {
    if (watcher.muted) {
      continue;
    }
    wishes.push({
      userId: watcher.userId,
      kind: 'status',
      sourceKind: 'task',
      sourceId: String(task.id),
      taskId: task.id,
      channelId: null,
      preview: `Задача ${task.key} перешла в статус «${statusLabel(task.status)}»`,
    });
  }
}

export function taskSetStatusWishes({ cache, event, after, wishes }: FanoutContext): void {
  const task = cache.tasks.get(event.entityId as TaskId);
  if (task === undefined) {
    return;
  }
  const status = after['status'];
  if (typeof status !== 'string') {
    return;
  }
  if (isTerminal(status)) {
    pushStatusWishes(cache, wishes, task);
  } else {
    pushTaskChanged(
      wishes,
      taskChangeWatchers(cache, task.id),
      task,
      `${task.key}: ${taskFieldLabel('status')} → ${statusLabel(task.status)}`,
    );
  }
}

export function taskSetAssigneeWishes({ cache, event, after, wishes }: FanoutContext): void {
  const task = cache.tasks.get(event.entityId as TaskId);
  const assignee = after['assigneeId'];
  if (task !== undefined && typeof assignee === 'string') {
    wishes.push({
      userId: assignee as unknown as UserId,
      kind: 'assigned',
      sourceKind: 'task',
      sourceId: String(task.id),
      taskId: task.id,
      channelId: null,
      preview: `Вас назначили исполнителем задачи ${task.key} — «${task.title}»`,
    });
  }
}

export function taskSetMilestoneWishes({ cache, event, after, wishes }: FanoutContext): void {
  const task = cache.tasks.get(event.entityId as TaskId);
  const milestoneId = after['milestoneId'];
  if (task === undefined || typeof milestoneId !== 'string') {
    return;
  }
  const milestone = cache.milestones.get(milestoneId as unknown as MilestoneId);
  for (const watcher of watchersOf(cache, { kind: 'milestone', id: milestoneId as unknown as MilestoneId })) {
    if (watcher.muted) {
      continue;
    }
    wishes.push({
      userId: watcher.userId,
      kind: 'task_added',
      sourceKind: 'task',
      sourceId: String(task.id),
      taskId: task.id,
      channelId: null,
      preview: `Задача ${task.key} добавлена в «${milestone?.name ?? 'веха'}»`,
    });
  }
}

export function taskSetTitleWishes({ cache, event, wishes }: FanoutContext): void {
  const task = cache.tasks.get(event.entityId as TaskId);
  if (task === undefined) {
    return;
  }
  pushTaskChanged(
    wishes,
    taskChangeWatchers(cache, task.id),
    task,
    `${task.key}: ${taskFieldLabel('title')} изменён`,
  );
}

export function taskSetDescriptionWishes({ cache, event, wishes }: FanoutContext): void {
  const task = cache.tasks.get(event.entityId as TaskId);
  if (task === undefined) {
    return;
  }
  pushTaskChanged(
    wishes,
    taskChangeWatchers(cache, task.id),
    task,
    `${task.key}: ${taskFieldLabel('description')} изменено`,
  );
}

export function taskSetPriorityWishes({ cache, event, after, wishes }: FanoutContext): void {
  const task = cache.tasks.get(event.entityId as TaskId);
  const priority = after['priority'];
  if (task === undefined || typeof priority !== 'string') {
    return;
  }
  const label = PRIORITY_LABELS[priority as Priority] ?? priority;
  pushTaskChanged(
    wishes,
    taskChangeWatchers(cache, task.id),
    task,
    `${task.key}: ${taskFieldLabel('priority')} → ${label}`,
  );
}

export function taskSetEstimateWishes({ cache, event, after, wishes }: FanoutContext): void {
  const task = cache.tasks.get(event.entityId as TaskId);
  if (task === undefined) {
    return;
  }
  const estimate = after['estimateMinutes'];
  const value = typeof estimate === 'number' ? formatMinutes(estimate) : 'снята';
  pushTaskChanged(
    wishes,
    taskChangeWatchers(cache, task.id),
    task,
    `${task.key}: ${taskFieldLabel('estimate')} → ${value}`,
  );
}

export function taskSetDatesWishes({ cache, event, after, wishes }: FanoutContext): void {
  const task = cache.tasks.get(event.entityId as TaskId);
  if (task === undefined) {
    return;
  }
  const parts: string[] = [];
  if ('dueOn' in after) {
    const dueOn = after['dueOn'];
    parts.push(`${taskFieldLabel('dueOn')} → ${typeof dueOn === 'string' ? formatDayRu(dueOn as DayStr) : 'снят'}`);
  }
  if ('startOn' in after) {
    const startOn = after['startOn'];
    parts.push(`${taskFieldLabel('startOn')} → ${typeof startOn === 'string' ? formatDayRu(startOn as DayStr) : 'снято'}`);
  }
  if (parts.length === 0) {
    return;
  }
  pushTaskChanged(wishes, taskChangeWatchers(cache, task.id), task, `${task.key}: ${parts.join(', ')}`);
}

export function taskSetLabelsWishes({ cache, event, wishes }: FanoutContext): void {
  const task = cache.tasks.get(event.entityId as TaskId);
  if (task === undefined) {
    return;
  }
  pushTaskChanged(
    wishes,
    taskChangeWatchers(cache, task.id),
    task,
    `${task.key}: ${taskFieldLabel('labels')} изменены`,
  );
}

export function taskSetTypeWishes({ cache, event, after, wishes }: FanoutContext): void {
  const task = cache.tasks.get(event.entityId as TaskId);
  if (task === undefined) {
    return;
  }
  const typeId = after['typeId'];
  const typeName = typeof typeId === 'string' ? cache.taskTypes.get(typeId as unknown as TaskTypeId)?.name : undefined;
  pushTaskChanged(
    wishes,
    taskChangeWatchers(cache, task.id),
    task,
    `${task.key}: ${taskFieldLabel('type')} → ${typeName ?? 'снят'}`,
  );
}

export function taskMoveWishes({ cache, event, wishes }: FanoutContext): void {
  const task = cache.tasks.get(event.entityId as TaskId);
  if (task === undefined) {
    return;
  }
  const beforePayload = event.before ?? {};
  const beforeProjectId = beforePayload['projectId'];
  const beforeStatus = beforePayload['status'];
  const projectChanged = typeof beforeProjectId === 'string' && String(task.projectId) !== beforeProjectId;
  const statusChanged = typeof beforeStatus === 'string' && task.status !== beforeStatus;
  const becameTerminal = statusChanged && isTerminal(task.status) && !isTerminal(beforeStatus as string);
  if (becameTerminal) {
    pushStatusWishes(cache, wishes, task);
  } else {
    const parts: string[] = [];
    if (projectChanged) {
      const project = cache.projects.get(task.projectId);
      parts.push(`${taskFieldLabel('project')} → ${project?.name ?? 'другой проект'}`);
    }
    if (statusChanged) {
      parts.push(`${taskFieldLabel('status')} → ${statusLabel(task.status)}`);
    }
    if (parts.length > 0) {
      pushTaskChanged(wishes, taskChangeWatchers(cache, task.id), task, `${task.key}: ${parts.join(', ')}`);
    }
  }
}
